import {
  useMutation,
  type UseMutationResult,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";
import { EndpointService, type ID } from "@/types/api/api";

export interface ISalesLimit {
  item_count: number;
  low_stock_limit: number;
  item_id: number;
}

export interface IListSalesLimitsResponse extends Array<ISalesLimit> {}

export interface ICreateSalesLimitParams {
  item_count: number;
  low_stock_limit: number;
  item_id: number;
}

export interface IUpdateSalesLimitParams extends ICreateSalesLimitParams {}

export interface IDeleteSalesLimitParams {
  item_id: ID;
}

export interface ISalesLimitsService {
  list: () => UseQueryResult<IListSalesLimitsResponse, Error>;
  retrieve: (id: ID) => UseQueryResult<ISalesLimit, Error>;
  create: () => UseMutationResult<ISalesLimit, Error, ICreateSalesLimitParams>;
  update: () => UseMutationResult<ISalesLimit, Error, IUpdateSalesLimitParams>;
  delete: () => UseMutationResult<boolean, Error, IDeleteSalesLimitParams>;
}

export class SalesLimitsService implements ISalesLimitsService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(): UseQueryResult<IListSalesLimitsResponse, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "sales-limits"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IListSalesLimitsResponse>
        >("/sales_limits/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: {},
        });

        return data;
      },
    });
  }

  create(): UseMutationResult<ISalesLimit, Error, ICreateSalesLimitParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "sales-limits"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<AxiosResponse<ISalesLimit>>(
          "/sales_limits/",
          {
            params,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
    });
  }

  retrieve(id: ID): UseQueryResult<ISalesLimit, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "sales-limits", id],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<ISalesLimit>>(
          `/sales_limits/${id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
    });
  }

  update(): UseMutationResult<ISalesLimit, Error, IUpdateSalesLimitParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "sales-limits", "update"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { item_id, ...restParams } = params;
        const { data } = await this.api.patch<AxiosResponse<ISalesLimit>>(
          `/sales_limits/${item_id}/`,
          {
            params: restParams,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      onMutate: async (newSalesLimit) => {},
      onError: (err, newSalesLimit, context) => {},
      onSettled: (data, error, variables) => {},
    });
  }

  delete(): UseMutationResult<boolean, Error, IDeleteSalesLimitParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "sales-limits", "delete"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        await this.api.delete<AxiosResponse<boolean>>(
          `/sales_limits/${params.item_id}/`,
          { endpointService: EndpointService.Gateway, venueSpecific: false },
        );
        return true;
      },
      onMutate: async (newSalesLimit) => {},
      onError: (err, newSalesLimit, context) => {
        return false;
      },
      onSettled: (data, error, variables) => {},
    });
  }
}
